import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  Slider,
} from '@mui/material';
import { CenterFocusStrong as AutoFitIcon, ZoomIn as ZoomInIcon, ZoomOut as ZoomOutIcon } from '@mui/icons-material';

interface ImageCropModalProps {
  open: boolean;
  onClose: () => void;
  imageFile: File | null;
  onCropComplete: (croppedImageBlob: Blob) => void;
  aspectRatio?: number;
}

const ImageCropModal: React.FC<ImageCropModalProps> = ({
  open,
  onClose,
  imageFile,
  onCropComplete,
  aspectRatio = 1,
}) => {
  console.log('ImageCropModal render:', { open, imageFile: !!imageFile, aspectRatio });
  const [imageSrc, setImageSrc] = useState<string>('');
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (imageFile && open) {
      console.log('Loading image file:', imageFile.name);
      const reader = new FileReader();
      reader.onload = () => {
        console.log('Image loaded successfully');
        setImageSrc(reader.result as string);
        setError(null);
      };
      reader.onerror = () => {
        console.error('Failed to load image');
        setError('Failed to load image');
      };
      reader.readAsDataURL(imageFile);
    }
  }, [imageFile, open]);

  const getCroppedImg = async (image: HTMLImageElement): Promise<Blob> => {
    console.log('Creating cropped image with transformations...');
    console.log('Current zoom:', zoom, 'Current pan:', pan);

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('No 2d context');
    }

    // Final output size (square for logo compatibility)
    const outputSize = 300;
    canvas.width = outputSize;
    canvas.height = outputSize;

    // Fill with white background to avoid transparency issues
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, outputSize, outputSize);

    // Get the container dimensions to match the preview
    const containerWidth = containerRef.current?.clientWidth || 400;
    const containerHeight = containerRef.current?.clientHeight || 400;
    const overlaySize = 300; // Size of the circular overlay

    // Calculate how the image is displayed in the preview (object-fit: contain)
    const imageAspect = image.naturalWidth / image.naturalHeight;
    const containerAspect = containerWidth / containerHeight;
    
    let displayWidth, displayHeight;
    let offsetX = 0, offsetY = 0;

    if (imageAspect > containerAspect) {
      // Image is wider than container
      displayWidth = containerWidth;
      displayHeight = containerWidth / imageAspect;
      offsetY = (containerHeight - displayHeight) / 2;
    } else {
      // Image is taller than container or same aspect
      displayHeight = containerHeight;
      displayWidth = containerHeight * imageAspect;
      offsetX = (containerWidth - displayWidth) / 2;
    }

    // Apply zoom to display dimensions
    const zoomedDisplayWidth = displayWidth * zoom;
    const zoomedDisplayHeight = displayHeight * zoom;

    // Calculate the center of the container
    const containerCenterX = containerWidth / 2;
    const containerCenterY = containerHeight / 2;

    // Calculate the center of the overlay (crop area)
    const overlayCenterX = containerCenterX;
    const overlayCenterY = containerCenterY;

    // Calculate the top-left corner of the zoomed image in container coordinates
    const imageLeft = containerCenterX - (zoomedDisplayWidth / 2) + pan.x;
    const imageTop = containerCenterY - (zoomedDisplayHeight / 2) + pan.y;

    // Calculate the crop area in image display coordinates
    const cropLeft = overlayCenterX - (overlaySize / 2) - imageLeft;
    const cropTop = overlayCenterY - (overlaySize / 2) - imageTop;
    const cropWidth = overlaySize;
    const cropHeight = overlaySize;

    // Convert from display coordinates to source image coordinates
    const scaleX = image.naturalWidth / zoomedDisplayWidth;
    const scaleY = image.naturalHeight / zoomedDisplayHeight;

    const sourceX = cropLeft * scaleX;
    const sourceY = cropTop * scaleY;
    const sourceWidth = cropWidth * scaleX;
    const sourceHeight = cropHeight * scaleY;

    console.log('Fixed calculations:', {
      containerWidth, containerHeight,
      displayWidth, displayHeight,
      zoomedDisplayWidth, zoomedDisplayHeight,
      imageLeft, imageTop,
      cropLeft, cropTop,
      sourceX, sourceY, sourceWidth, sourceHeight
    });

    // Ensure source rectangle is within image bounds
    const clampedSourceX = Math.max(0, Math.min(image.naturalWidth - 1, sourceX));
    const clampedSourceY = Math.max(0, Math.min(image.naturalHeight - 1, sourceY));
    const clampedSourceWidth = Math.min(sourceWidth, image.naturalWidth - clampedSourceX);
    const clampedSourceHeight = Math.min(sourceHeight, image.naturalHeight - clampedSourceY);

    // Ensure we have valid dimensions
    if (clampedSourceWidth <= 0 || clampedSourceHeight <= 0) {
      console.warn('Invalid crop dimensions, using fallback');
      // Fallback: crop from center of image
      const centerX = image.naturalWidth / 2;
      const centerY = image.naturalHeight / 2;
      const size = Math.min(image.naturalWidth, image.naturalHeight);
      
      ctx.drawImage(
        image,
        centerX - size / 2,
        centerY - size / 2,
        size,
        size,
        0,
        0,
        outputSize,
        outputSize
      );
    } else {
      console.log('Final source rectangle:', { 
        clampedSourceX, clampedSourceY, 
        clampedSourceWidth, clampedSourceHeight 
      });

      // Draw the cropped image
      ctx.drawImage(
        image,
        clampedSourceX,
        clampedSourceY,
        clampedSourceWidth,
        clampedSourceHeight,
        0,
        0,
        outputSize,
        outputSize
      );
    }

    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) {
          console.log('Blob created successfully, size:', blob.size);
          resolve(blob);
        } else {
          reject(new Error('Failed to create blob'));
        }
      }, 'image/jpeg', 0.95);
    });
  };

  const handleApply = async () => {
    if (!imgRef.current) {
      console.error('No image reference');
      return;
    }

    console.log('Applying crop...');
    setLoading(true);
    setError(null);

    try {
      const croppedImageBlob = await getCroppedImg(imgRef.current);
      console.log('Crop completed, calling onCropComplete...');
      onCropComplete(croppedImageBlob);
      onClose();
    } catch (err) {
      console.error('Error cropping image:', err);
      setError('Failed to crop image. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    console.log('Cancelling crop modal...');
    setImageSrc('');
    setError(null);
    onClose();
  };

  // Mouse event handlers for dragging/panning
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!containerRef.current) return;
    setIsDragging(true);
    setDragStart({
      x: e.clientX - pan.x,
      y: e.clientY - pan.y
    });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !containerRef.current) return;

    const newPan = {
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    };

    // More generous pan boundaries for better positioning control
    const maxPan = 200 * zoom; // Increased boundary for more movement
    newPan.x = Math.max(-maxPan, Math.min(maxPan, newPan.x));
    newPan.y = Math.max(-maxPan, Math.min(maxPan, newPan.y));

    setPan(newPan);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Keyboard controls for precise positioning
  const handleKeyDown = (e: KeyboardEvent) => {
    if (!imgRef.current) return;

    const step = 5; // pixels per key press
    let newPan = { ...pan };

    switch (e.key) {
      case 'ArrowUp':
        newPan.y = Math.min(200 * zoom, pan.y + step);
        break;
      case 'ArrowDown':
        newPan.y = Math.max(-200 * zoom, pan.y - step);
        break;
      case 'ArrowLeft':
        newPan.x = Math.max(-200 * zoom, pan.x + step);
        break;
      case 'ArrowRight':
        newPan.x = Math.min(200 * zoom, pan.x - step);
        break;
      default:
        return;
    }

    e.preventDefault();
    setPan(newPan);
  };

  // Add keyboard event listener
  React.useEffect(() => {
    if (open) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [open, pan, zoom]);

  // Reset pan when zoom changes
  React.useEffect(() => {
    setPan({ x: 0, y: 0 });
  }, [zoom]);

  console.log('Rendering ImageCropModal with open:', open);
  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh' }
      }}
    >
      <DialogTitle>
        <Typography variant="h6" fontWeight="bold">
          Edit Logo
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Drag to position, use arrow keys for precise movement, zoom to adjust. The circle shows your logo area.
        </Typography>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {imageSrc && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box
              ref={containerRef}
              sx={{
                width: '100%',
                height: '400px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                mb: 2,
                position: 'relative',
                cursor: isDragging ? 'grabbing' : 'grab'
              }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              <img
                ref={imgRef}
                src={imageSrc}
                alt="Logo preview"
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                  transformOrigin: 'center center',
                  transition: isDragging ? 'none' : 'transform 0.2s ease-in-out',
                  pointerEvents: 'none'
                }}
                onLoad={() => console.log('Preview image loaded')}
                draggable={false}
              />
              {/* Circular overlay to show crop area (matches logo display) */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '300px',
                  height: '300px',
                  border: '2px dashed',
                  borderColor: 'primary.main',
                  borderRadius: '50%',
                  pointerEvents: 'none'
                }}
              />
            </Box>

            {/* Zoom Controls */}
            <Box sx={{ width: '100%', mb: 2 }}>
              <Typography variant="body2" gutterBottom sx={{ textAlign: 'center' }}>
                Zoom: {Math.round(zoom * 100)}%
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Button
                  size="small"
                  onClick={() => setZoom(Math.max(0.25, zoom - 0.25))}
                  disabled={zoom <= 0.25}
                >
                  <ZoomOutIcon />
                </Button>
                <Slider
                  value={zoom}
                  min={0.25}
                  max={3}
                  step={0.25}
                  onChange={(_, value) => setZoom(value as number)}
                  aria-labelledby="zoom-slider"
                  sx={{ flex: 1 }}
                />
                <Button
                  size="small"
                  onClick={() => setZoom(Math.min(3, zoom + 0.25))}
                  disabled={zoom >= 3}
                >
                  <ZoomInIcon />
                </Button>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                <Button
                  size="small"
                  onClick={() => setZoom(1)}
                  variant="outlined"
                >
                  Reset Zoom
                </Button>
                <Button
                  size="small"
                  onClick={() => setPan({ x: 0, y: 0 })}
                  variant="outlined"
                >
                  Reset Position
                </Button>
              </Box>
            </Box>

            {/* Controls Info */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AutoFitIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="body2" color="text.secondary">
                    Auto-fit mode: Image will be centered and cropped to square format
                  </Typography>
                </Box>
              </Box>

              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                p: 1,
                bgcolor: 'background.paper',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider'
              }}>
                <Typography variant="caption" color="text.secondary">
                  Keyboard: ↑↓←→ for precise positioning
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleApply}
          variant="contained"
          disabled={loading || !imageSrc}
        >
          {loading ? 'Processing...' : 'Apply Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageCropModal;
